import { useState, useEffect } from "react";
import { 
  BarChart3, TrendingUp, Users, Building2, DollarSign, Calendar,
  ArrowUpRight, ArrowDownRight, Target, CheckCircle2, Clock, AlertCircle,
  CalendarDays, Trophy, Medal, Award, MousePointer, FileCheck, Package
} from "lucide-react";
import { 
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import PlantaoModal from "../components/PlantaoModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Configuração de períodos
const PERIODOS = [
  { value: 'dia', label: 'Hoje', days: 1 },
  { value: 'semana', label: 'Esta Semana', days: 7 },
  { value: 'mes', label: 'Este Mês', days: 30 },
  { value: 'trimestre', label: 'Trimestre', days: 90 },
  { value: 'semestre', label: 'Semestre', days: 180 },
  { value: 'ano', label: 'Este Ano', days: 365 }
];

const Dashboard = ({ onLogout }) => {
  const [periodo, setPeriodo] = useState('mes');
  const [stats, setStats] = useState({
    totalClientes: 0,
    totalCaptacoes: 0,
    totalEmpreendimentos: 0,
    vgvTotal: 0,
    agendamentosHoje: 0,
    negociacoes: 0,
    clientesNoPeriodo: 0,
    captacoesNoPeriodo: 0,
    vgvNoPeriodo: 0,
    totalVendas: 0,
    vendasNoPeriodo: 0
  });
  const [captacoesPorStatus, setCaptacoesPorStatus] = useState([]);
  const [clientesPorMes, setClientesPorMes] = useState([]);
  const [captacoesPorMes, setCaptacoesPorMes] = useState([]);
  const [rankingCorretores, setRankingCorretores] = useState({
    vendas: [],
    interacoes: [],
    captacoes: [],
    cadastrosCompletos: []
  });
  const [loading, setLoading] = useState(true);
  const [showPlantaoModal, setShowPlantaoModal] = useState(false);
  const [avisos, setAvisos] = useState({ plantao: null, rodizio: null });
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    document.title = "Central de Lançamentos | CRM";
    const user = JSON.parse(localStorage.getItem('user'));
    setCurrentUser(user);
    fetchDashboardData();
    
    // Verificar avisos de plantão/rodízio
    if (user?.role === 'proprietario' || user?.role === 'admin' || user?.role === 'gestor') {
      verificarAvisos();
    }
  }, [periodo]);

  const verificarAvisos = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${BACKEND_URL}/api/plantao/avisos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvisos(res.data);
      
      // Mostrar modal se precisar configurar plantão
      if (res.data?.plantao?.mostrar_aviso) {
        setShowPlantaoModal(true);
      }
    } catch (error) {
      console.error("Erro ao verificar avisos:", error);
    }
  };

  // Manter compatibilidade com verificação antiga
  const verificarPlantao = async () => {
    verificarAvisos();
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [clientesRes, captacoesRes, empreendimentosRes, agendamentosRes, usersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/clients`, { headers }),
        axios.get(`${BACKEND_URL}/api/captacoes`, { headers }),
        axios.get(`${BACKEND_URL}/api/empreendimentos`, { headers }),
        axios.get(`${BACKEND_URL}/api/agendamentos`, { headers }),
        axios.get(`${BACKEND_URL}/api/users`, { headers }).catch(() => ({ data: [] }))
      ]);

      const clientes = clientesRes.data;
      const captacoes = captacoesRes.data;
      const empreendimentos = empreendimentosRes.data;
      const agendamentos = agendamentosRes.data;
      const usuarios = usersRes.data || [];

      // Obter configuração do período selecionado
      const periodoConfig = PERIODOS.find(p => p.value === periodo);
      const diasAtras = periodoConfig?.days || 30;
      const dataInicio = new Date();
      dataInicio.setDate(dataInicio.getDate() - diasAtras);
      dataInicio.setHours(0, 0, 0, 0);

      // Filtrar dados pelo período
      const clientesNoPeriodo = clientes.filter(c => {
        if (!c.created_at) return false;
        return new Date(c.created_at) >= dataInicio;
      });

      const captacoesNoPeriodo = captacoes.filter(c => {
        if (!c.created_at) return false;
        return new Date(c.created_at) >= dataInicio;
      });

      // Calcular VGV apenas de imóveis VENDIDOS (vendas concluídas)
      const captacoesVendidas = captacoes.filter(c => c.status === 'vendido');
      const captacoesVendidasNoPeriodo = captacoesNoPeriodo.filter(c => c.status === 'vendido');
      
      const vgvTotal = captacoesVendidas.reduce((acc, cap) => acc + (cap.valor || 0), 0);
      const vgvNoPeriodo = captacoesVendidasNoPeriodo.reduce((acc, cap) => acc + (cap.valor || 0), 0);
      const totalVendas = captacoesVendidas.length;
      const vendasNoPeriodo = captacoesVendidasNoPeriodo.length;

      // Agendamentos de hoje
      const hoje = new Date().toISOString().split('T')[0];
      const agendamentosHoje = agendamentos.filter(a => 
        a.data_visita && a.data_visita.startsWith(hoje)
      ).length;

      // Negociações em andamento (no período)
      const negociacoes = captacoesNoPeriodo.filter(c => 
        c.status === 'negociacao' || c.status === 'proposta'
      ).length;

      setStats({
        totalClientes: clientes.length,
        totalCaptacoes: captacoes.length,
        totalEmpreendimentos: empreendimentos.length,
        vgvTotal,
        agendamentosHoje,
        negociacoes,
        clientesNoPeriodo: clientesNoPeriodo.length,
        captacoesNoPeriodo: captacoesNoPeriodo.length,
        vgvNoPeriodo,
        totalVendas,
        vendasNoPeriodo
      });

      // Dados para gráfico de pizza - Captações por status (no período)
      const statusCount = {};
      captacoesNoPeriodo.forEach(cap => {
        const status = cap.status || 'disponivel';
        statusCount[status] = (statusCount[status] || 0) + 1;
      });
      
      const statusLabels = {
        disponivel: 'Disponível',
        reservado: 'Reservado',
        vendido: 'Vendido',
        negociacao: 'Negociação',
        proposta: 'Proposta',
        em_disputa: 'Em Disputa'
      };
      
      setCaptacoesPorStatus(Object.entries(statusCount).map(([key, value]) => ({
        name: statusLabels[key] || key,
        value,
        status: key
      })));

      // Dados para gráfico de área - Clientes por período
      const mesesGrafico = getMesesPorPeriodo(periodo);
      const clientesPorMesData = getDataPorMes(clientes, 'created_at', mesesGrafico);
      setClientesPorMes(clientesPorMesData);

      // Dados para gráfico de barras - Captações por período
      const captacoesPorMesData = getDataPorMes(captacoes, 'created_at', mesesGrafico);
      setCaptacoesPorMes(captacoesPorMesData);

      // ==================== RANKING DE CORRETORES ====================
      // Criar mapa de usuários para buscar nomes
      const usuariosMap = {};
      usuarios.forEach(u => {
        usuariosMap[u.id] = u.name || u.email || 'Desconhecido';
      });

      // 1. RANKING POR VENDAS (VGV de imóveis vendidos)
      const vendasPorCorretor = {};
      captacoesVendidas.forEach(cap => {
        const corretorId = cap.captador_id || cap.created_by;
        if (corretorId) {
          if (!vendasPorCorretor[corretorId]) {
            vendasPorCorretor[corretorId] = { id: corretorId, vendas: 0, vgv: 0 };
          }
          vendasPorCorretor[corretorId].vendas += 1;
          vendasPorCorretor[corretorId].vgv += (cap.valor || 0);
        }
      });
      const rankingVendas = Object.values(vendasPorCorretor)
        .map(c => ({ ...c, nome: usuariosMap[c.id] || 'Corretor' }))
        .sort((a, b) => b.vgv - a.vgv)
        .slice(0, 5);

      // 2. RANKING POR INTERAÇÕES (clientes atribuídos + agendamentos)
      const interacoesPorCorretor = {};
      clientes.forEach(cli => {
        const corretorId = cli.assigned_to || cli.created_by;
        if (corretorId) {
          if (!interacoesPorCorretor[corretorId]) {
            interacoesPorCorretor[corretorId] = { id: corretorId, interacoes: 0 };
          }
          interacoesPorCorretor[corretorId].interacoes += 1;
          // Contar interações (histórico)
          if (cli.historico && Array.isArray(cli.historico)) {
            interacoesPorCorretor[corretorId].interacoes += cli.historico.length;
          }
        }
      });
      agendamentos.forEach(ag => {
        const corretorId = ag.corretor_id || ag.created_by;
        if (corretorId) {
          if (!interacoesPorCorretor[corretorId]) {
            interacoesPorCorretor[corretorId] = { id: corretorId, interacoes: 0 };
          }
          interacoesPorCorretor[corretorId].interacoes += 1;
        }
      });
      const rankingInteracoes = Object.values(interacoesPorCorretor)
        .map(c => ({ ...c, nome: usuariosMap[c.id] || 'Corretor' }))
        .sort((a, b) => b.interacoes - a.interacoes)
        .slice(0, 5);

      // 3. RANKING POR CAPTAÇÕES (total de imóveis captados)
      const captacoesPorCorretor = {};
      captacoes.forEach(cap => {
        const corretorId = cap.captador_id || cap.created_by;
        if (corretorId) {
          if (!captacoesPorCorretor[corretorId]) {
            captacoesPorCorretor[corretorId] = { id: corretorId, captacoes: 0, valorTotal: 0 };
          }
          captacoesPorCorretor[corretorId].captacoes += 1;
          captacoesPorCorretor[corretorId].valorTotal += (cap.valor || 0);
        }
      });
      const rankingCaptacoes = Object.values(captacoesPorCorretor)
        .map(c => ({ ...c, nome: usuariosMap[c.id] || 'Corretor' }))
        .sort((a, b) => b.captacoes - a.captacoes)
        .slice(0, 5);

      // 4. RANKING POR CADASTROS COMPLETOS (captações com documentação completa)
      const cadastrosCompletosPorCorretor = {};
      captacoes.forEach(cap => {
        // Verificar se tem documentação completa
        const temDocumentos = cap.matricula_imovel || cap.autorizacao_venda_url || cap.documentos_proprietario_url;
        const temDadosCompletos = cap.proprietario_nome && cap.proprietario_telefone && cap.metragem && cap.dormitorios;
        
        if (temDocumentos || temDadosCompletos) {
          const corretorId = cap.captador_id || cap.created_by;
          if (corretorId) {
            if (!cadastrosCompletosPorCorretor[corretorId]) {
              cadastrosCompletosPorCorretor[corretorId] = { id: corretorId, completos: 0 };
            }
            cadastrosCompletosPorCorretor[corretorId].completos += 1;
          }
        }
      });
      const rankingCadastrosCompletos = Object.values(cadastrosCompletosPorCorretor)
        .map(c => ({ ...c, nome: usuariosMap[c.id] || 'Corretor' }))
        .sort((a, b) => b.completos - a.completos)
        .slice(0, 5);

      setRankingCorretores({
        vendas: rankingVendas,
        interacoes: rankingInteracoes,
        captacoes: rankingCaptacoes,
        cadastrosCompletos: rankingCadastrosCompletos
      });

    } catch (error) {
      console.error("Erro ao carregar dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  // Determinar quantos meses mostrar no gráfico baseado no período
  const getMesesPorPeriodo = (periodo) => {
    switch(periodo) {
      case 'dia': return 7; // últimos 7 dias no gráfico
      case 'semana': return 4; // últimas 4 semanas
      case 'mes': return 6;
      case 'trimestre': return 6;
      case 'semestre': return 6;
      case 'ano': return 12;
      default: return 6;
    }
  };

  const getDataPorMes = (data, dateField, meses) => {
    const result = [];
    const now = new Date();
    
    for (let i = meses - 1; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const mesAno = date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
      const count = data.filter(item => {
        if (!item[dateField]) return false;
        const itemDate = new Date(item[dateField]);
        return itemDate.getMonth() === date.getMonth() && 
               itemDate.getFullYear() === date.getFullYear();
      }).length;
      
      result.push({ mes: mesAno, quantidade: count });
    }
    
    return result;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899'];
  
  const getStatusColor = (status) => {
    const colors = {
      disponivel: '#10b981',
      reservado: '#f59e0b',
      vendido: '#3b82f6',
      negociacao: '#8b5cf6',
      proposta: '#ec4899',
      em_disputa: '#ef4444'
    };
    return colors[status] || '#94a3b8';
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900">
        <Sidebar onLogout={onLogout} />
        <main className="flex-1 flex items-center justify-center pt-20 lg:pt-0">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900">
      <Sidebar onLogout={onLogout} />
      
      {/* Modal de Plantão */}
      <PlantaoModal 
        isOpen={showPlantaoModal} 
        onClose={() => setShowPlantaoModal(false)}
        currentUser={currentUser}
      />
      
      <main className="flex-1 p-4 lg:p-6 overflow-y-auto pt-20 lg:pt-6">
        <div className="w-full">
          {/* Header com Filtro de Período */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="text-left">
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-white text-left">
                Dashboard
              </h1>
              <p className="text-slate-500 dark:text-slate-400 text-left">
                Visão geral do seu negócio imobiliário
              </p>
            </div>
            
            {/* Seletor de Período */}
            <div className="flex items-center gap-2 bg-white dark:bg-slate-900 rounded-xl p-1.5 shadow-lg border border-slate-200 dark:border-slate-700">
              <CalendarDays className="w-4 h-4 text-slate-400 ml-2" />
              <div className="flex flex-wrap gap-1">
                {PERIODOS.map((p) => (
                  <button
                    key={p.value}
                    onClick={() => setPeriodo(p.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      periodo === p.value
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Avisos de Plantão/Rodízio */}
          {avisos?.plantao?.mostrar_aviso && (
            <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex items-start gap-3" data-testid="aviso-plantao">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  Configure o Plantão do Fim de Semana
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  {avisos.plantao.mensagem}
                </p>
                {avisos.plantao.dias_faltando && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    Dias pendentes: {avisos.plantao.dias_faltando.join(", ")}
                  </p>
                )}
              </div>
              <button 
                onClick={() => setShowPlantaoModal(true)}
                className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium"
              >
                Configurar
              </button>
            </div>
          )}
          
          {avisos?.rodizio?.mostrar_aviso && (
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl flex items-start gap-3" data-testid="aviso-rodizio">
              <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-blue-800 dark:text-blue-200">
                  Configure o Rodízio de Hoje
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  {avisos.rodizio.mensagem}
                </p>
              </div>
              <a 
                href="/crm/equipe?tab=rodizio"
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
              >
                Configurar
              </a>
            </div>
          )}

          {/* KPIs Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 bg-blue-100 dark:bg-blue-500/20 rounded-xl">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                  {PERIODOS.find(p => p.value === periodo)?.label}
                </span>
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.clientesNoPeriodo}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">Novos Clientes</p>
              <p className="text-xs text-slate-400 mt-1">Total: {stats.totalClientes}</p>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 bg-emerald-100 dark:bg-emerald-500/20 rounded-xl">
                  <Building2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                  {PERIODOS.find(p => p.value === periodo)?.label}
                </span>
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.captacoesNoPeriodo}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">Novas Captações</p>
              <p className="text-xs text-slate-400 mt-1">Total: {stats.totalCaptacoes}</p>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 bg-purple-100 dark:bg-purple-500/20 rounded-xl">
                  <DollarSign className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <span className="text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/20 px-2 py-0.5 rounded-full font-medium">
                  Vendidos
                </span>
              </div>
              <p className="text-xl font-bold text-slate-900 dark:text-white">{formatCurrency(stats.vgvNoPeriodo)}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">VGV Comercializado</p>
              <p className="text-xs text-slate-400 mt-1">
                {stats.vendasNoPeriodo} vendas no período | Total: {formatCurrency(stats.vgvTotal)} ({stats.totalVendas} vendas)
              </p>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 bg-amber-100 dark:bg-amber-500/20 rounded-xl">
                  <Target className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.negociacoes}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">Em Negociação</p>
              <p className="text-xs text-slate-400 mt-1">No período selecionado</p>
            </div>
          </div>

          {/* Charts Row 1 */}
          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            {/* Gráfico de Área - Clientes */}
            <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-lg border border-slate-200 dark:border-slate-700">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-4">
                Novos Clientes por Mês
                <span className="text-xs font-normal text-slate-500 dark:text-slate-400 ml-2">
                  ({PERIODOS.find(p => p.value === periodo)?.label})
                </span>
              </h3>
              <div className="h-64">
                {clientesPorMes.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <AreaChart data={clientesPorMes}>
                      <defs>
                        <linearGradient id="colorClientes" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="mes" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1e293b', 
                          border: 'none', 
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="quantidade" 
                        stroke="#3b82f6" 
                        fillOpacity={1} 
                        fill="url(#colorClientes)" 
                        name="Clientes"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 dark:text-slate-500 text-sm">
                    Sem dados no período
                  </div>
                )}
              </div>
            </div>

            {/* Gráfico de Pizza - Status das Captações */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-lg border border-slate-200 dark:border-slate-700">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-4">
                Captações por Status
                <span className="text-xs font-normal text-slate-500 dark:text-slate-400 ml-2">
                  ({PERIODOS.find(p => p.value === periodo)?.label})
                </span>
              </h3>
              <div className="h-64">
                {captacoesPorStatus.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <PieChart>
                      <Pie
                        data={captacoesPorStatus}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {captacoesPorStatus.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getStatusColor(entry.status)} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1e293b', 
                          border: 'none', 
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                      />
                      <Legend 
                        verticalAlign="bottom" 
                        height={36}
                        wrapperStyle={{ fontSize: '12px', color: '#64748b' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 dark:text-slate-500 text-sm">
                    Nenhuma captação no período
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            {/* Gráfico de Barras - Captações por Mês */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-lg border border-slate-200 dark:border-slate-700">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-4">
                Captações por Mês
                <span className="text-xs font-normal text-slate-500 dark:text-slate-400 ml-2">
                  ({PERIODOS.find(p => p.value === periodo)?.label})
                </span>
              </h3>
              <div className="h-64">
                {captacoesPorMes.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <BarChart data={captacoesPorMes}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="mes" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1e293b', 
                          border: 'none', 
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                      />
                      <Bar dataKey="quantidade" fill="#10b981" radius={[4, 4, 0, 0]} name="Captações" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 dark:text-slate-500 text-sm">
                    Sem dados no período
                  </div>
                )}
              </div>
            </div>

            {/* Cards de Atividades */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-lg border border-slate-200 dark:border-slate-700">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-4">
                Resumo de Hoje
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                  <div className="p-3 bg-blue-100 dark:bg-blue-500/20 rounded-xl">
                    <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.agendamentosHoje}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Visitas agendadas para hoje</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                  <div className="p-3 bg-purple-100 dark:bg-purple-500/20 rounded-xl">
                    <Target className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.negociacoes}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Negociações em andamento</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                  <div className="p-3 bg-emerald-100 dark:bg-emerald-500/20 rounded-xl">
                    <Building2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.totalEmpreendimentos}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Empreendimentos ativos</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ranking de Corretores */}
          <div className="mb-6">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Ranking de Corretores
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Ranking por Vendas (VGV) */}
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 bg-emerald-100 dark:bg-emerald-500/20 rounded-lg">
                    <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white text-sm">Maiores Vendas</h3>
                </div>
                <div className="space-y-3">
                  {rankingCorretores.vendas.length > 0 ? (
                    rankingCorretores.vendas.map((corretor, idx) => (
                      <div key={corretor.id} className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-amber-400 text-amber-900' :
                          idx === 1 ? 'bg-slate-300 text-slate-700' :
                          idx === 2 ? 'bg-amber-600 text-amber-100' :
                          'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{corretor.nome}</p>
                          <p className="text-xs text-slate-500">{corretor.vendas} vendas</p>
                        </div>
                        <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(corretor.vgv)}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">Nenhuma venda registrada</p>
                  )}
                </div>
              </div>

              {/* Ranking por Interações */}
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 bg-blue-100 dark:bg-blue-500/20 rounded-lg">
                    <MousePointer className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white text-sm">Mais Interações</h3>
                </div>
                <div className="space-y-3">
                  {rankingCorretores.interacoes.length > 0 ? (
                    rankingCorretores.interacoes.map((corretor, idx) => (
                      <div key={corretor.id} className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-amber-400 text-amber-900' :
                          idx === 1 ? 'bg-slate-300 text-slate-700' :
                          idx === 2 ? 'bg-amber-600 text-amber-100' :
                          'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{corretor.nome}</p>
                        </div>
                        <p className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                          {corretor.interacoes} ações
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">Nenhuma interação registrada</p>
                  )}
                </div>
              </div>

              {/* Ranking por Captações */}
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 bg-purple-100 dark:bg-purple-500/20 rounded-lg">
                    <Package className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white text-sm">Mais Captações</h3>
                </div>
                <div className="space-y-3">
                  {rankingCorretores.captacoes.length > 0 ? (
                    rankingCorretores.captacoes.map((corretor, idx) => (
                      <div key={corretor.id} className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-amber-400 text-amber-900' :
                          idx === 1 ? 'bg-slate-300 text-slate-700' :
                          idx === 2 ? 'bg-amber-600 text-amber-100' :
                          'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{corretor.nome}</p>
                          <p className="text-xs text-slate-500">{formatCurrency(corretor.valorTotal)}</p>
                        </div>
                        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400">
                          {corretor.captacoes} imóveis
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">Nenhuma captação registrada</p>
                  )}
                </div>
              </div>

              {/* Ranking por Cadastros Completos */}
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 bg-amber-100 dark:bg-amber-500/20 rounded-lg">
                    <FileCheck className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white text-sm">Cadastros Completos</h3>
                </div>
                <div className="space-y-3">
                  {rankingCorretores.cadastrosCompletos.length > 0 ? (
                    rankingCorretores.cadastrosCompletos.map((corretor, idx) => (
                      <div key={corretor.id} className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-amber-400 text-amber-900' :
                          idx === 1 ? 'bg-slate-300 text-slate-700' :
                          idx === 2 ? 'bg-amber-600 text-amber-100' :
                          'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{corretor.nome}</p>
                        </div>
                        <p className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                          {corretor.completos} completos
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">Nenhum cadastro completo</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
